import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { PageHeader, SectionCard } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Bell, Lock, Palette, LifeBuoy, LogOut, Trash2, User, Sun, Moon, Monitor } from "lucide-react";

function ToggleRow({ label, description, defaultChecked, testid }) {
  const [on, setOn] = useState(defaultChecked);
  return (
    <div className="flex items-center justify-between py-3">
      <div><p className="text-sm font-medium text-slate-800">{label}</p>{description && <p className="text-xs text-slate-400">{description}</p>}</div>
      <Switch checked={on} onCheckedChange={setOn} data-testid={testid} />
    </div>
  );
}

export default function Settings() {
  const { logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);

  const doLogout = async () => { await logout(); navigate("/"); };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/account");
      toast.success("Your account has been deleted.");
      await logout();
      navigate("/");
    } catch (e) { toast.error(errMsg(e)); } finally { setDeleting(false); }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader title="Settings" subtitle="Manage your account and preferences." />

      <SectionCard title={<span className="flex items-center gap-2"><User className="w-4 h-4 text-primary" /> Account</span>} className="mb-5">
        <p className="text-sm text-slate-500">Update your personal details on the <button onClick={() => navigate("/profile")} className="text-primary font-medium" data-testid="settings-goto-profile">Profile</button> page.</p>
      </SectionCard>

      <SectionCard title={<span className="flex items-center gap-2"><Bell className="w-4 h-4 text-primary" /> Notifications</span>} className="mb-5">
        <div className="divide-y divide-slate-100">
          <ToggleRow label="Delivery & offer alerts" description="Get notified about offers and status changes" defaultChecked testid="setting-notif-offers" />
          <ToggleRow label="New messages" description="Notify me when I receive a message" defaultChecked testid="setting-notif-messages" />
          <ToggleRow label="Product updates" description="Occasional news about Pack Mate" testid="setting-notif-updates" />
        </div>
      </SectionCard>

      <SectionCard title={<span className="flex items-center gap-2"><Lock className="w-4 h-4 text-primary" /> Privacy</span>} className="mb-5">
        <div className="divide-y divide-slate-100">
          <ToggleRow label="Show verification badge" description="Display your verified status to others" defaultChecked testid="setting-privacy-badge" />
          <ToggleRow label="Discoverable trips" description="Allow senders to find your published trips" defaultChecked testid="setting-privacy-discover" />
        </div>
      </SectionCard>

      <SectionCard title={<span className="flex items-center gap-2"><Palette className="w-4 h-4 text-primary" /> Appearance</span>} className="mb-5">
        <p className="text-sm text-slate-500 mb-3">Choose how Pack Mate looks. Your choice is saved on this device.</p>
        <div className="grid grid-cols-3 gap-3">
          {[["light", "Light", Sun], ["dark", "Dark", Moon], ["system", "System", Monitor]].map(([val, label, Icon]) => (
            <button
              key={val}
              onClick={() => setTheme(val)}
              data-testid={`theme-${val}`}
              className={`flex flex-col items-center gap-2 rounded-xl border p-4 transition-colors ${theme === val ? "border-primary bg-primary/5 text-primary" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            >
              <Icon className="w-5 h-5" />
              <span className="text-sm font-medium">{label}</span>
            </button>
          ))}
        </div>
      </SectionCard>

      <SectionCard title={<span className="flex items-center gap-2"><LifeBuoy className="w-4 h-4 text-primary" /> Help &amp; Security</span>} className="mb-5">
        <div className="flex flex-col gap-2">
          <Button variant="outline" onClick={() => navigate("/support")} className="rounded-xl justify-start" data-testid="settings-support-btn">Visit Support</Button>
          <Button variant="outline" onClick={() => navigate("/safety")} className="rounded-xl justify-start" data-testid="settings-safety-btn">Safety Center</Button>
        </div>
      </SectionCard>

      <SectionCard title={<span className="flex items-center gap-2 text-rose-600"><Trash2 className="w-4 h-4" /> Danger zone</span>}>
        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="outline" onClick={doLogout} className="rounded-xl" data-testid="settings-signout-btn"><LogOut className="w-4 h-4" /> Sign Out</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="rounded-xl" data-testid="delete-account-btn"><Trash2 className="w-4 h-4" /> Delete Account</Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="delete-account-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure you want to delete your account?</AlertDialogTitle>
                <AlertDialogDescription>
                  This may be permanent. Your active trips and open delivery requests will be cancelled. Some information may be retained where required or permitted by law. See our <a href="https://sites.google.com/view/pack-mate/delete-account" target="_blank" rel="noreferrer" className="text-primary underline">account deletion policy</a>.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={deleteAccount} disabled={deleting} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-confirm-btn">{deleting ? "Deleting…" : "Yes, delete my account"}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </SectionCard>
    </div>
  );
}
